import {
	Button,
	Checkbox,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	Info,
	Input,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useState } from "react";
import { Async, Badge, Field, PageHead, useAction, useConfirm } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Pack, type Plan } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

const M = 1_000_000;

export function PlansPage() {
	const t = useT();
	const { locale } = useI18n();
	const plans = useAsync(() => api.plans(), []);
	const markup = useAsync(() => api.markup(), []);
	const packs = useAsync(() => api.packs(), []);
	const [editing, setEditing] = useState<Plan | null>(null);
	const [pack, setPack] = useState<Pack | null | undefined>(undefined);

	return (
		<>
			<PageHead title={t("plans.title")} lede={t("plans.lede")} />
			<p className="hint" style={{ marginBottom: "var(--s-8)" }}>
				{t("plans.perDollar", { n: 100 })}
			</p>
			<Async state={plans}>
				{({ plans: list, orgs_on }) => (
					<div className="table-scroll">
						<Table className="text-[length:var(--t-sm)]">
							<TableHeader>
								<TableRow>
									<TableHead>{t("plans.title")}</TableHead>
									<TableHead className="num text-right whitespace-nowrap">{t("plans.credits")}</TableHead>
									<TableHead className="num text-right whitespace-nowrap">{t("plans.priceUsd")}</TableHead>
									<TableHead className="num text-right whitespace-nowrap">{t("plans.priceCny")}</TableHead>
									<TableHead className="num text-right whitespace-nowrap">{t("plans.accounts")}</TableHead>
									<TableHead className="num text-right whitespace-nowrap">{t("plans.position")}</TableHead>
									<TableHead className="text-right">{t("state.actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{list.map((plan) => (
									<TableRow key={plan.key}>
										<TableCell>
											{locale === "zh" ? plan.name_zh : plan.name_en} <span className="mono faint">{plan.key}</span>
											<span className="sub">
												<span className="chips">
													<Badge tone={plan.active ? "neutral" : "outline"}>
														{t(plan.active ? "plans.active" : "plans.inactive")}
													</Badge>
													{plan.contact_only ? <Badge tone="neutral">{t("plans.contactOnly")}</Badge> : null}
													{plan.trial ? <Badge tone="neutral">{t("plans.trial")}</Badge> : null}
												</span>
											</span>
										</TableCell>
										<TableCell className="num text-right whitespace-nowrap">
											{fmt.number(locale, plan.monthly_credits)}
										</TableCell>
										<TableCell className="num text-right whitespace-nowrap">
											{fmt.usd(locale, plan.price_usd_micros)}
										</TableCell>
										<TableCell className="num text-right whitespace-nowrap">
											{fmt.cny(locale, plan.price_cny_micros)}
										</TableCell>
										<TableCell className="num text-right whitespace-nowrap">
											{orgs_on.find((o) => o.plan_key === plan.key)?.count ?? 0}
										</TableCell>
										<TableCell className="num text-right whitespace-nowrap">{plan.position}</TableCell>
										<TableCell>
											<div className="row-actions">
												<Button size="sm" variant="ghost" type="button" onClick={() => setEditing(plan)}>
													{t("state.edit")}
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</Async>
			{editing ? (
				<PlanModal
					key={editing.key}
					plan={editing}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setEditing(null);
						plans.reload();
					}}
				/>
			) : null}

			<div className="section">
				<h2>
					{t("plans.markup")} <Info>{t("plans.markupHint")}</Info>
				</h2>
				<Async state={markup}>
					{({ credit_markup_bp }) => <MarkupForm bp={credit_markup_bp} onSaved={markup.reload} />}
				</Async>
			</div>

			<div className="section">
				<div className="table-tools">
					<h2>{t("plans.packs")}</h2>
					<span className="spacer" />
					<Button variant="default" type="button" onClick={() => setPack(null)}>
						{t("plans.newPack")}
					</Button>
				</div>
				<Async state={packs}>
					{({ packs: list }) => (
						<div className="table-scroll">
							<Table className="text-[length:var(--t-sm)]">
								<TableHeader>
									<TableRow>
										<TableHead>{t("plans.packKey")}</TableHead>
										<TableHead className="num text-right whitespace-nowrap">{t("plans.packCredits")}</TableHead>
										<TableHead className="num text-right whitespace-nowrap">USD</TableHead>
										<TableHead className="num text-right whitespace-nowrap">CNY</TableHead>
										<TableHead>{t("plans.priceIds")}</TableHead>
										<TableHead className="num text-right whitespace-nowrap">{t("plans.position")}</TableHead>
										<TableHead>{t("plans.active")}</TableHead>
										<TableHead className="text-right">{t("state.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{list.map((row) => (
										<TableRow key={row.key}>
											<TableCell className="mono">{row.key}</TableCell>
											<TableCell className="num text-right whitespace-nowrap">
												{fmt.number(locale, row.credits)}
											</TableCell>
											<TableCell className="num text-right whitespace-nowrap">
												{fmt.usd(locale, row.price_usd_micros)}
											</TableCell>
											<TableCell className="num text-right whitespace-nowrap">
												{fmt.cny(locale, row.price_cny_micros)}
											</TableCell>
											<TableCell className="mono small">
												{row.price_id_usd ?? "—"} / {row.price_id_cny ?? "—"}
											</TableCell>
											<TableCell className="num text-right whitespace-nowrap">{row.position}</TableCell>
											<TableCell>
												<Badge tone={row.active ? "success" : "outline"}>
													{t(row.active ? "state.yes" : "state.no")}
												</Badge>
											</TableCell>
											<TableCell>
												<div className="row-actions">
													<Button size="sm" variant="ghost" type="button" onClick={() => setPack(row)}>
														{t("state.edit")}
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</Async>
			</div>
			{pack !== undefined ? (
				<PackModal
					key={pack?.key ?? "new"}
					pack={pack}
					onClose={() => setPack(undefined)}
					onSaved={() => {
						setPack(undefined);
						packs.reload();
					}}
				/>
			) : null}
		</>
	);
}

/**
 * One plan's thirty knobs. They are a modal rather than a card per plan
 * because every plan's form on one page was five forms deep and the operator
 * could not tell which "Save" belonged to which plan.
 */
function PlanModal({ plan, onClose, onSaved }: { plan: Plan; onClose: () => void; onSaved: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const [p, setP] = useState<Plan>(plan);
	const set = <K extends keyof Plan>(key: K, value: Plan[K]) => setP((prev) => ({ ...prev, [key]: value }));
	const num = (key: keyof Plan) => (e: React.ChangeEvent<HTMLInputElement>) =>
		set(key, Number(e.target.value) as never);
	const money = (key: keyof Plan) => (e: React.ChangeEvent<HTMLInputElement>) =>
		set(key, Math.round(Number(e.target.value) * M) as never);
	const text = (key: keyof Plan) => (e: React.ChangeEvent<HTMLInputElement>) => set(key, e.target.value as never);

	async function submit() {
		const r = await run(
			() => api.putPlan(plan.key, p),
			() => t("state.saved"),
		);
		if (r) onSaved();
	}

	return (
		<ChipModal open size="xl" dismissDisabled={busy} srTitle={t("plans.editPlan")} onOpenChange={onClose}>
			<ChipModalHeader onClose={onClose}>
				{locale === "zh" ? p.name_zh : p.name_en} <span className="mono faint">{p.key}</span>
			</ChipModalHeader>
			<ChipModalBody className="console-form-body">
				<fieldset className="form" disabled={busy}>
					<ChipModalField type="custom" title={t("plans.naming")}>
						<div className="field-row">
							<Field label={t("plans.nameEn")}>
								<Input value={p.name_en} onChange={text("name_en")} />
							</Field>
							<Field label={t("plans.nameZh")}>
								<Input value={p.name_zh} onChange={text("name_zh")} />
							</Field>
							<Field label={t("plans.position")}>
								<Input type="number" value={p.position} onChange={num("position")} />
							</Field>
						</div>
					</ChipModalField>
					<ChipModalField type="custom" title={t("plans.limits")}>
						<div className="field-row">
							<Field label={t("plans.credits")}>
								<Input type="number" min={0} value={p.monthly_credits} onChange={num("monthly_credits")} />
							</Field>
							<Field label={t("plans.entities")}>
								<Input type="number" min={0} value={p.entities_max} onChange={num("entities_max")} />
							</Field>
							<Field label={t("plans.engines")}>
								<Input type="number" min={0} value={p.engines_max} onChange={num("engines_max")} />
							</Field>
							<Field label={t("plans.seats")}>
								<Input type="number" min={0} value={p.seats_max} onChange={num("seats_max")} />
							</Field>
							<Field label={t("plans.interval")}>
								<Input type="number" min={0} value={p.sample_interval_days} onChange={num("sample_interval_days")} />
							</Field>
							<Field label={t("plans.replication")}>
								<Input type="number" min={0} max={5} value={p.replication} onChange={num("replication")} />
							</Field>
							<Field label={t("plans.questions")}>
								<Input type="number" min={0} value={p.questions_max} onChange={num("questions_max")} />
							</Field>
							<Field label={t("plans.workflows")}>
								<Input type="number" min={0} value={p.workflows_max} onChange={num("workflows_max")} />
							</Field>
							<Field label={t("plans.pagesMonth")}>
								<Input type="number" min={0} value={p.pages_per_month} onChange={num("pages_per_month")} />
							</Field>
							<Field label={t("plans.reviewFirst")}>
								<Input type="number" min={0} value={p.review_first_n} onChange={num("review_first_n")} />
							</Field>
						</div>
					</ChipModalField>
					<ChipModalField type="custom" title={t("plans.features")}>
						<div className="row">
							<Label className="check">
								<Checkbox checked={p.active} onCheckedChange={(checked) => set("active", checked === true)} />
								{t("plans.active")}
							</Label>
							<Label className="check">
								<Checkbox
									checked={p.contact_only}
									onCheckedChange={(checked) => set("contact_only", checked === true)}
								/>
								{t("plans.contactOnly")}
							</Label>
							<Label className="check">
								<Checkbox checked={p.cname} onCheckedChange={(checked) => set("cname", checked === true)} />
								{t("plans.cname")}
							</Label>
							<Label className="check">
								<Checkbox checked={p.trial} onCheckedChange={(checked) => set("trial", checked === true)} />
								{t("plans.trial")}
							</Label>
							<Label className="check">
								<Checkbox
									checked={p.advanced_insights}
									onCheckedChange={(checked) => set("advanced_insights", checked === true)}
								/>
								{t("plans.advanced")}
							</Label>
							<Label className="check">
								<Checkbox
									checked={p.review_always}
									onCheckedChange={(checked) => set("review_always", checked === true)}
								/>
								{t("plans.reviewAlways")}
							</Label>
						</div>
					</ChipModalField>
					<ChipModalField
						type="custom"
						title={t("plans.prices")}
						hint={`${fmt.usd(locale, p.price_usd_micros)} / ${fmt.cny(locale, p.price_cny_micros)}`}
					>
						<div className="field-row field-row-pairs">
							<Field label={t("plans.priceUsd")}>
								<Input
									type="number"
									min={0}
									step="0.01"
									value={p.price_usd_micros / M}
									onChange={money("price_usd_micros")}
								/>
							</Field>
							<Field label={t("plans.priceUsdYear")}>
								<Input
									type="number"
									min={0}
									step="0.01"
									value={p.price_usd_yearly_micros / M}
									onChange={money("price_usd_yearly_micros")}
								/>
							</Field>
							<Field label={t("plans.priceCny")}>
								<Input
									type="number"
									min={0}
									step="0.01"
									value={p.price_cny_micros / M}
									onChange={money("price_cny_micros")}
								/>
							</Field>
							<Field label={t("plans.priceCnyYear")}>
								<Input
									type="number"
									min={0}
									step="0.01"
									value={p.price_cny_yearly_micros / M}
									onChange={money("price_cny_yearly_micros")}
								/>
							</Field>
						</div>
					</ChipModalField>
					<ChipModalField type="custom" title={t("plans.priceIds")}>
						<div className="field-row field-row-pairs">
							<Field label={t("plans.priceUsd")}>
								<Input value={p.price_id_usd ?? ""} onChange={(e) => set("price_id_usd", e.target.value)} />
							</Field>
							<Field label={t("plans.priceUsdYear")}>
								<Input
									value={p.price_id_usd_yearly ?? ""}
									onChange={(e) => set("price_id_usd_yearly", e.target.value)}
								/>
							</Field>
							<Field label={t("plans.priceCny")}>
								<Input value={p.price_id_cny ?? ""} onChange={(e) => set("price_id_cny", e.target.value)} />
							</Field>
							<Field label={t("plans.priceCnyYear")}>
								<Input
									value={p.price_id_cny_yearly ?? ""}
									onChange={(e) => set("price_id_cny_yearly", e.target.value)}
								/>
							</Field>
						</div>
					</ChipModalField>
				</fieldset>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				primaryAction={{ label: t("state.save"), onClick: () => void submit(), disabled: busy }}
			/>
		</ChipModal>
	);
}

/// The one number that turns what the models charge into what a customer is
/// charged. Basis points, so 10000 is list price exactly.
function MarkupForm({ bp, onSaved }: { bp: number; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [value, setValue] = useState(String(bp));
	const parsed = Number(value);
	const dirty = parsed !== bp && parsed > 0;
	const times = (n: number) => (n / 10000).toFixed(2);
	return (
		<div className="row">
			<div className="field-num-lg">
				<Input
					aria-label={t("plans.markup")}
					type="number"
					min={1}
					max={100000}
					value={value}
					onChange={(e) => setValue(e.target.value)}
				/>
			</div>
			<span className="hint">{t("plans.markupTimes", { n: (parsed / 10000).toFixed(2) })}</span>
			<Button
				variant="default"
				type="button"
				disabled={busy || !dirty}
				onClick={async () => {
					const ok = await confirm(
						t("plans.markup"),
						t("plans.markupConfirm", { from: times(bp), to: times(parsed) }),
						true,
					);
					if (!ok) return;
					run(
						() => api.putMarkup({ credit_markup_bp: parsed }),
						() => t("state.saved"),
					).then(onSaved);
				}}
			>
				{t("state.save")}
			</Button>
		</div>
	);
}

/** A pack, new or existing. The key is set once and read thereafter. */
function PackModal({ pack, onClose, onSaved }: { pack: Pack | null; onClose: () => void; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const [key, setKey] = useState(pack?.key ?? "");
	const [credits, setCredits] = useState(String(pack?.credits ?? 1000));
	const [usd, setUsd] = useState(String((pack?.price_usd_micros ?? 10 * M) / M));
	const [cny, setCny] = useState(String((pack?.price_cny_micros ?? 70 * M) / M));
	const [idUsd, setIdUsd] = useState(pack?.price_id_usd ?? "");
	const [idCny, setIdCny] = useState(pack?.price_id_cny ?? "");
	const [position, setPosition] = useState(String(pack?.position ?? 0));
	const [active, setActive] = useState(pack?.active ?? true);

	async function save() {
		const body = {
			credits: Number(credits),
			price_usd_micros: Math.round(Number(usd) * M),
			price_cny_micros: Math.round(Number(cny) * M),
			price_id_usd: idUsd || null,
			price_id_cny: idCny || null,
			position: Number(position),
			active,
		};
		const r = await run(
			() => (pack ? api.putPack(pack.key, body) : api.newPack({ key, ...body })),
			() => t("state.saved"),
		);
		if (r) onSaved();
	}

	return (
		<ChipModal
			open
			size="lg"
			dismissDisabled={busy}
			srTitle={t(pack ? "plans.editPack" : "plans.newPack")}
			onOpenChange={onClose}
		>
			<ChipModalHeader onClose={onClose}>{t(pack ? "plans.editPack" : "plans.newPack")}</ChipModalHeader>
			<ChipModalBody className="console-form-body">
				<fieldset className="form" disabled={busy}>
					{pack ? (
						<ChipModalField type="copy" title={t("plans.packKey")} value={pack.key} />
					) : (
						<ChipModalField type="input" title={t("plans.packKey")} required mono value={key} onChange={setKey} />
					)}
					<ChipModalField type="custom" title={t("plans.prices")}>
						<div className="field-row">
							<Field label={t("plans.packCredits")}>
								<Input type="number" min={1} value={credits} onChange={(e) => setCredits(e.target.value)} />
							</Field>
							<Field label="USD">
								<Input type="number" min={0} step="0.01" value={usd} onChange={(e) => setUsd(e.target.value)} />
							</Field>
							<Field label="CNY">
								<Input type="number" min={0} step="0.01" value={cny} onChange={(e) => setCny(e.target.value)} />
							</Field>
							<Field label={t("plans.position")}>
								<Input type="number" value={position} onChange={(e) => setPosition(e.target.value)} />
							</Field>
						</div>
					</ChipModalField>
					<ChipModalField type="custom" title={t("plans.priceIds")}>
						<div className="field-row">
							<Field label="USD">
								<Input value={idUsd} onChange={(e) => setIdUsd(e.target.value)} />
							</Field>
							<Field label="CNY">
								<Input value={idCny} onChange={(e) => setIdCny(e.target.value)} />
							</Field>
						</div>
					</ChipModalField>
					<ChipModalField type="custom" title={t("plans.features")}>
						<Label className="check">
							<Checkbox checked={active} onCheckedChange={(checked) => setActive(checked === true)} />
							{t("plans.active")}
						</Label>
					</ChipModalField>
				</fieldset>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				primaryAction={{
					label: t(pack ? "state.save" : "plans.newPack"),
					onClick: () => void save(),
					disabled: busy || (!pack && !key.trim()),
				}}
			/>
		</ChipModal>
	);
}
